'use client'

import { useState } from 'react'
import { Product } from '@/lib/types'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ProductQuickView } from '@/components/product-quick-view'
import Image from 'next/image'
import Link from 'next/link'
import { Eye, Download, FileText, Layers, Package, Cpu, Scale, Heart } from 'lucide-react'
import { toast } from 'sonner'
import { useComparison } from '@/lib/comparison-context'
import { useWishlist } from '@/lib/wishlist-context'
import { useQuoteCart } from '@/lib/quote-cart-context'
import { cn } from '@/lib/utils'

interface ProductCardProps {
  product: Product
  variant?: 'grid' | 'list'
}

export function ProductCard({ product, variant = 'grid' }: ProductCardProps) {
  const [quickViewOpen, setQuickViewOpen] = useState(false)
  const { isInComparison, addToComparison, removeFromComparison, comparedProducts } = useComparison()
  const { isInWishlist, toggleWishlist } = useWishlist()
  const { addToQuoteCart, isInQuoteCart } = useQuoteCart()
  const isCompared = isInComparison(product.id)
  const canAddMore = comparedProducts.length < 4
  const isWishlisted = isInWishlist(product.id)
  const inQuoteCart = isInQuoteCart(product.id)

  const handleRequestQuote = () => {
    addToQuoteCart(product, 1)
    toast.success(`${product.partNumber} added to quote request`, {
      description: 'Click the cart icon to continue',
      action: {
        label: 'View Cart',
        onClick: () => window.location.href = '/quote'
      }
    })
  }

  const handleQuickView = () => {
    setQuickViewOpen(true)
  }

  const handleDownloadDatasheet = () => {
    toast.info('Datasheet download feature coming soon')
  }

  const handleCompareToggle = () => {
    if (isCompared) {
      removeFromComparison(product.id)
      toast.success('Removed from comparison')
    } else if (canAddMore) {
      addToComparison(product)
      toast.success('Added to comparison')
    } else {
      toast.error('Maximum 4 products can be compared')
    }
  }

  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    toggleWishlist(product)
    if (isWishlisted) {
      toast.success('Removed from wishlist')
    } else {
      toast.success('Added to wishlist')
    }
  }

  const Specs = () => (
    <div className="text-xs text-slate-600 space-y-1 mt-2">
      <div className="flex items-center gap-2">
        <Layers className="w-3 h-3 text-slate-500" />
        <span>Category: {product.category}</span>
      </div>
      <div className="flex items-center gap-2">
        <Package className="w-3 h-3 text-slate-500" />
        <span>Package: {product.packageType}</span>
      </div>
      {product.specifications['Core'] && (
        <div className="flex items-center gap-2">
          <Cpu className="w-3 h-3 text-slate-500" />
          <span>Core: {product.specifications['Core']}</span>
        </div>
      )}
    </div>
  )

  if (variant === 'list') {
    return (
      <>
        <Card className="mb-4 hover:shadow-lg transition-shadow duration-300">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
              {/* Product Image */}
              <div className="md:col-span-2 flex justify-center">
                <div className="relative w-24 h-24 bg-slate-50 rounded-lg overflow-hidden shrink-0">
                  <Image
                    src={product.images[0] || '/placeholder.jpg'}
                    alt={product.title}
                    fill
                    className="object-contain p-2"
                  />
                </div>
              </div>

              {/* Product Info */}
              <div className="md:col-span-5 min-w-0">
                <div className="space-y-2">
                  <Link href={`/products/${product.id}`} className="group">
                    <h3 className="font-semibold text-base text-slate-800 group-hover:text-blue-600 transition-colors">{product.partNumber}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2">{product.description}</p>
                  </Link>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span>by <span className="font-medium text-slate-700">{product.manufacturer}</span></span>
                  </div>
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <Badge variant={product.stock === 'In Stock' ? 'default' : 'destructive'}>
                      {product.stock}
                    </Badge>
                    {product.rohs && (
                      <Badge variant="outline">RoHS</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Specs & Actions */}
              <div className="md:col-span-5 min-w-0">
                <div className="space-y-3">
                  {/* Specs */}
                  <div className="text-xs text-slate-600 space-y-1">
                    <div className="flex items-center gap-2">
                      <Layers className="w-3 h-3 text-slate-500 shrink-0" />
                      <span className="truncate">Category: {product.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="w-3 h-3 text-slate-500 shrink-0" />
                      <span className="truncate">Package: {product.packageType}</span>
                    </div>
                    {product.specifications['Core'] && (
                      <div className="flex items-center gap-2">
                        <Cpu className="w-3 h-3 text-slate-500 shrink-0" />
                        <span className="truncate">Core: {product.specifications['Core']}</span>
                      </div>
                    )}
                  </div>

                  {/* Wishlist & Compare Controls */}
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className={cn(
                        "h-8 transition-all duration-200 min-w-0",
                        isWishlisted 
                          ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100" 
                          : "bg-white hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                      )}
                      onClick={handleWishlistToggle}
                      aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                    >
                      <Heart className={cn("w-3 h-3 mr-1.5", isWishlisted && "fill-current")} />
                      <span className="text-xs truncate">Wishlist</span>
                    </Button>
                    
                    <label className="flex items-center gap-1.5 cursor-pointer group/compare h-8 px-3 rounded-md border border-slate-200 hover:border-[#518BC4] hover:bg-slate-50 transition-colors min-w-0">
                      <Checkbox
                        checked={isCompared}
                        onCheckedChange={handleCompareToggle}
                        disabled={!canAddMore && !isCompared}
                        className="data-[state=checked]:bg-[#518BC4] data-[state=checked]:border-[#518BC4]"
                      />
                      <Scale className="h-3 w-3 text-slate-600 group-hover/compare:text-[#518BC4] transition-colors" />
                      <span className="text-xs text-slate-600 group-hover/compare:text-[#518BC4] transition-colors truncate">
                        Compare
                      </span>
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col lg:flex-row gap-2 flex-wrap">
                    <Button size="sm" onClick={handleRequestQuote} className="w-full lg:flex-1">
                      Request Quote
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleQuickView}
                      className="w-full lg:flex-1"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Quick View
                    </Button>
                    <Button variant="outline" size="sm" asChild className="w-full lg:flex-1">
                      <Link href={`/products/${product.id}`}>
                        <FileText className="w-4 h-4 mr-2" />
                        Details
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <ProductQuickView 
          product={product} 
          open={quickViewOpen} 
          onOpenChange={setQuickViewOpen} 
        />
      </>
    )
  }

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full relative">
      <CardHeader className="p-4">
        <div className="relative aspect-square bg-slate-50 rounded-lg overflow-hidden mb-3">
          <Image
            src={product.images[0] || '/placeholder.jpg'}
            alt={product.title}
            fill
            className="object-contain p-4 group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-2 left-2">
            <div className="flex flex-col gap-1">
              <Badge variant={product.stock === 'In Stock' ? 'default' : 'destructive'}>
                {product.stock}
              </Badge>
              {product.rohs && (
                <Badge variant="outline">RoHS</Badge>
              )}
            </div>
          </div>
            <div className="absolute top-2 right-2 flex gap-1">
            <Button 
              variant="outline" 
              size="icon" 
              className={cn(
                "h-8 w-8 transition-all duration-200",
                isWishlisted 
                  ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100" 
                  : "bg-white hover:bg-red-50 hover:border-red-200 hover:text-red-600 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
              )}
              onClick={handleWishlistToggle}
              aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
            >
              <Heart className={cn("w-4 h-4", isWishlisted && "fill-current")} />
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8 bg-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto" 
              onClick={handleQuickView}
              aria-label="Quick view"
            >
              <Eye className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Compare Checkbox - Bottom Right of Image */}
          <div className="absolute bottom-2 right-2">
            <label className="flex items-center gap-1.5 bg-white/95 backdrop-blur-sm rounded-md px-2 py-1.5 cursor-pointer hover:bg-white transition-colors shadow-sm">
              <Checkbox
                checked={isCompared}
                onCheckedChange={handleCompareToggle}
                disabled={!canAddMore && !isCompared}
                className="data-[state=checked]:bg-[#518BC4] data-[state=checked]:border-[#518BC4]"
              />
              <span className="text-xs font-medium text-slate-700">
                Compare
              </span>
            </label>
          </div>
        </div>
        
        <div className="space-y-1">
          <p className="text-xs text-slate-500">{product.manufacturer}</p>
          <Link href={`/products/${product.id}`} className="group">
            <h3 className="font-semibold text-md text-slate-800 line-clamp-2 group-hover:text-blue-600 transition-colors">{product.partNumber}</h3>
          </Link>
          <p className="text-sm text-slate-600 line-clamp-2 h-10">{product.description}</p>
        </div>
      </CardHeader>
      
      <CardContent className="p-4 pt-0 flex-grow flex flex-col justify-end">
        <Specs />
        <div className="space-y-2 mt-4">
          <Button size="sm" onClick={handleRequestQuote} className="w-full">
            Request Quote
          </Button>
          <Button variant="outline" size="sm" asChild className="w-full">
            <Link href={`/products/${product.id}`}>
              View Details
            </Link>
          </Button>
          
          {/* {product.documents.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs"
              onClick={handleDownloadDatasheet}
            >
              <Download className="w-3 h-3 mr-1" />
              Download Datasheet
            </Button>
          )} */}
        </div>
      </CardContent>
      
      <ProductQuickView 
        product={product} 
        open={quickViewOpen} 
        onOpenChange={setQuickViewOpen} 
      />
    </Card>
  )
}
